import { ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { NotificationService } from '../../../services/messaging/notification.service';
import { AuthenticationService } from '../../../modules/auth/services/authentication.service';
import { TdoUserConfigurationModel } from '../../../models/configuration/user/tdo-user-configuration.model';
import { ColorSchemeDataModel } from '../../../models/configuration/color-scheme-data.model';
import { ColorSchemesService } from '../../../modules/configuration/color-schemes/color-schemes.service';
import { NotificationDataModel } from '../../../models/messaging/notification-data.model';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import {
  NotificationCreateDialogComponent
} from '../../../dialog/notifications/notification-create-dialog/notification-create-dialog.component';
import { NotificationDestinationDataModel } from '../../../models/messaging/notification-destination-data.model';
import { UiService } from '../../../services/ui.service';
import { UserService } from '../../../services/user.service';
import { Subject, interval } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'tdo-instant-messaging',
  templateUrl: './tdo-instant-messaging.component.html',
  styleUrls: ['./tdo-instant-messaging.component.scss']
})
export class TdoInstantMessagingComponent implements OnInit, OnDestroy {
  @Input() isDisableHeader: boolean = false;
  @Input() isMinimized: boolean = false;
  colorSchemes: ColorSchemeDataModel[] = [];
  users: TdoUserConfigurationModel[] = [];
  instantMessages: NotificationDestinationDataModel[] = [];
  hoveredThreadIconId: string | null = null;
  readonly PRIORITY_HIGH = '00000502-1000-0000-0000-000000000002';
  private readonly destroy$ = new Subject<void>();
  private knownMessageIds = new Set<string>();
  private usersLoaded = false;

  constructor(public notificationService: NotificationService,
    private readonly uiService: UiService,
    private readonly userService: UserService,
    private readonly authenticationService: AuthenticationService,
    private readonly colorSchemeService: ColorSchemesService,
    private readonly dialog: MatDialog,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.userService.getUsers(this.authenticationService.currentUserValue.TDOCID).subscribe((users) => {
      this.users = users;
      this.usersLoaded = true;
      this.processIncomingMessages(this.notificationService.instantMessages || []);
    });

    this.colorSchemeService.getColorSchemes().subscribe((cs) => {
      this.colorSchemes = cs;
    });

    this.notificationService.getMessagesForUser(this.authenticationService.currentUserValue.authdata.userid)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.instantMessages = this.notificationService.instantMessages || [];
        this.knownMessageIds = new Set(this.instantMessages.map(m => m.NotificationToID));
        this.cdr.detectChanges();
      });

    this.notificationService.messageUpdate
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.ngZone.run(() => {
          this.processIncomingMessages(this.notificationService.instantMessages || []);
          this.cdr.detectChanges();
        });
      });

    // Fallback poll: SignalR (Classic ASP.NET) is incompatible with the Core client and
    // never delivers push events. Poll every 10 s so new messages appear without a page refresh.
    interval(10000)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.notificationService.getMessagesForUser(this.authenticationService.currentUserValue.authdata.userid))
      )
      .subscribe(() => {
        this.ngZone.run(() => {
          this.processIncomingMessages(this.notificationService.instantMessages || []);
          this.cdr.detectChanges();
        });
      });
  }

  private processIncomingMessages(latest: NotificationDestinationDataModel[]): void {
    this.instantMessages = latest;
    if (!this.usersLoaded) {
      // Don't mark these as known yet: playSound() can't resolve a UserSound
      // until user config has loaded, so leave them pending for re-evaluation
      // once getUsers() completes (see ngOnInit).
      return;
    }
    const currentUserId = this.authenticationService.currentUserValue.authdata.userid;
    const hasNewIncoming = this.hasNewIncomingMessage(latest, currentUserId);
    this.knownMessageIds = new Set(latest.map(m => m.NotificationToID));
    if (hasNewIncoming) {
      this.playSound(currentUserId);
    }
  }

  private hasNewIncomingMessage(latest: NotificationDestinationDataModel[], currentUserId: string): boolean {
    return latest.some(m =>
      !this.knownMessageIds.has(m.NotificationToID) &&
      m.dataNotification?.SenderUserID?.toLowerCase() !== currentUserId?.toLowerCase()
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getInstantMessages() {
    this.notificationService.getMessagesForUser(this.authenticationService.currentUserValue.authdata.userid).subscribe((notifications) => {

    });
  }

  getBackgroundColor(userId: string) {
    if (this.users) {
      const user = this.users.find(p => p.UserID.toLowerCase() === userId.toLowerCase());
      if (user) {
        const scheme = this.colorSchemes.find(p => p.ColorSchemeId.toLowerCase() === user?.ColorSchemeID?.toLowerCase());
        if (scheme) {
          return scheme.BackColor;
        }
      }
    }
    return "white";
  }

  getForColor(userId: string) {
    if (this.users) {
      const user = this.users.find(p => p.UserID.toLowerCase() === userId.toLowerCase());
      if (user) {
        const scheme = this.colorSchemes.find(p => p.ColorSchemeId.toLowerCase() === user?.ColorSchemeID?.toLowerCase());
        if (scheme) {
          return scheme.ForeColor;
        }
      }
    }
    return "black";
  }

  getName(userId: string) {
    if (this.users) {
      if (userId.toLowerCase() === this.authenticationService?.currentUserValue?.authdata?.userid?.toLowerCase()) {
        return 'Me';
      }
      const user = this.users.find(p => p.UserID.toLowerCase() === userId.toLowerCase());
      if (user) {
        let ret = user.FirstName;
        if (user.LastName) {
          ret += ' ' + user.LastName;
        }
        return ret;
      }
    }
  }

  playSound(userId: string) {
    if (this.users) {
      const user = this.users.find(p => p.UserID.toLowerCase() === userId.toLowerCase());
      if (user) {
        const audio = new Audio();
        audio.src = 'assets/sounds/user/' + user.UserSound;
        audio.load();
        audio.play();
      }
    }
  }

  onNotificationSeenClick(notification: NotificationDataModel, b: boolean) {
    this.notificationService.markNotificationAsSeen(notification).subscribe((res) => {
      const idx = this.notificationService.instantMessages.findIndex(p => p.dataNotification.NotificationID === notification.NotificationID);
      if (idx > -1) {
        this.notificationService.instantMessages.splice(idx, 1);
        this.instantMessages = [...this.notificationService.instantMessages];
      }
    });
  }

  onOpenInstantMessaging(destination: NotificationDestinationDataModel = null) {
    const config = new MatDialogConfig();
    config.panelClass = 'dialog';
    config.data = destination;
    this.dialog.open(NotificationCreateDialogComponent, config);
  }

  getInitials(userId: string) {
    if (this.users) {

      const user = this.users.find(p => p.UserID.toLowerCase() === userId.toLowerCase());
      if (user) {
        let ret = user.FirstName.charAt(0);
        if (user.LastName) {
          ret += user.LastName.charAt(0);
        }
        return ret;
      }
    }
  }

  getMessageTime(s: string): string {
    const alreadyHasTz = /Z$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s);
    const d = new Date(alreadyHasTz ? s : s + 'Z');
    const now = new Date();
    if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()) {
      return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
    if (d.getFullYear() === now.getFullYear()) {
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ', ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { month: 'numeric', day: 'numeric', year: '2-digit' }) + ', ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  getDate(s: string) {
    const d = new Date(s);
    return d.toLocaleString();
  }

  isCurrentUser(userId: string): boolean {
    return userId?.toLowerCase() === this.authenticationService?.currentUserValue?.authdata?.userid?.toLowerCase();
  }
}
